import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import { apiRequest } from '@/lib/api'

// ===================
// Types
// ===================

export type GngCategory = string
export type ThemeCategory = 'experience' | 'personnel' | 'methodology' | 'innovation' | 'relationship' | 'value' | 'risk' | 'local_presence' | 'other'

export interface GngCategoryDef {
  slug: string
  label: string
  sortOrder: number
}

export interface GoNoGoTemplate {
  id: string
  organizationId: string
  thresholds: { go: number; conditionalGo: number; noGo: number }
  categories: GngCategoryDef[]
  criteria: GoNoGoTemplateCriterion[]
  createdAt: string
  updatedAt: string
}

export interface GoNoGoTemplateCriterion {
  id: string
  templateId: string
  category: GngCategory
  title: string
  description: string | null
  weight: number
  isMandatory: boolean
  isAiScorable: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface BankTheme {
  id: string
  organizationId: string
  title: string
  description: string | null
  category: ThemeCategory
  evidence: { type: string; detail: string }[]
  timesUsed: number
  timesWon: number
  createdBy: string
  createdAt: string
  updatedAt: string
}

// ===================
// Store
// ===================

interface StrategySettingsState {
  template: GoNoGoTemplate | null
  bankThemes: BankTheme[]
  isLoadingTemplate: boolean
  isLoadingBank: boolean
  isSavingTemplate: boolean
  isSavingBank: boolean
  error: string | null

  loadTemplate: () => Promise<void>
  saveTemplate: (data: {
    thresholds?: { go: number; conditionalGo: number; noGo: number }
    criteria?: {
      id?: string
      category: GngCategory
      title: string
      description?: string | null
      weight: number
      isMandatory: boolean
      isAiScorable: boolean
      sortOrder: number
    }[]
    categories?: GngCategoryDef[]
  }) => Promise<boolean>

  loadBankThemes: (filters?: { search?: string; category?: ThemeCategory }) => Promise<void>
  addBankTheme: (data: { title: string; description?: string | null; category: ThemeCategory }) => Promise<void>
  updateBankTheme: (id: string, data: { title?: string; description?: string | null; category?: ThemeCategory }) => Promise<void>
  removeBankTheme: (id: string) => Promise<void>

  clearError: () => void
}

export const useStrategySettingsStore = create<StrategySettingsState>()(
  devtools(
    (set) => ({
      template: null,
      bankThemes: [],
      isLoadingTemplate: false,
      isLoadingBank: false,
      isSavingTemplate: false,
      isSavingBank: false,
      error: null,

      loadTemplate: async () => {
        set({ isLoadingTemplate: true, error: null })
        try {
          const template = await apiRequest<GoNoGoTemplate>('/strategy/templates/go-no-go')
          set({ template, isLoadingTemplate: false })
        } catch (err) {
          set({ isLoadingTemplate: false, error: (err as Error).message })
        }
      },

      saveTemplate: async (data) => {
        set({ isSavingTemplate: true, error: null })
        try {
          const template = await apiRequest<GoNoGoTemplate>('/strategy/templates/go-no-go', {
            method: 'PUT',
            body: data,
          })
          set({ template, isSavingTemplate: false })
          return true
        } catch (err) {
          set({ isSavingTemplate: false, error: (err as Error).message })
          return false
        }
      },

      loadBankThemes: async (filters) => {
        set({ isLoadingBank: true, error: null })
        try {
          const params: Record<string, string> = {}
          if (filters?.search) params['search'] = filters.search
          if (filters?.category) params['category'] = filters.category
          const themes = await apiRequest<BankTheme[]>('/strategy/theme-bank', { params })
          set({ bankThemes: themes, isLoadingBank: false })
        } catch (err) {
          set({ isLoadingBank: false, error: (err as Error).message })
        }
      },

      addBankTheme: async (data) => {
        set({ isSavingBank: true, error: null })
        try {
          const theme = await apiRequest<BankTheme>('/strategy/theme-bank', {
            method: 'POST',
            body: data,
          })
          set(state => ({ bankThemes: [...state.bankThemes, theme], isSavingBank: false }))
        } catch (err) {
          set({ isSavingBank: false, error: (err as Error).message })
        }
      },

      updateBankTheme: async (id, data) => {
        set({ isSavingBank: true, error: null })
        try {
          const updated = await apiRequest<BankTheme>(`/strategy/theme-bank/${id}`, {
            method: 'PATCH',
            body: data,
          })
          set(state => ({
            bankThemes: state.bankThemes.map(t => t.id === id ? updated : t),
            isSavingBank: false,
          }))
        } catch (err) {
          set({ isSavingBank: false, error: (err as Error).message })
        }
      },

      removeBankTheme: async (id) => {
        set({ error: null })
        try {
          await apiRequest(`/strategy/theme-bank/${id}`, { method: 'DELETE' })
          set(state => ({ bankThemes: state.bankThemes.filter(t => t.id !== id) }))
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'strategy-settings-store' }
  )
)
